import { useState, useEffect } from "react";
import { LayoutTemplate, Eye, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAllTemplatesPreview } from "./hooks/useTemplates";
import { useCurrentUser } from "@/pages/auth/hooks/useAuth";
import { ExportCandidatesModal } from "@/pages/candidates/components/ExportCandidatesModal";

interface TemplateItem {
  id: string;
  name: string;
  html_template?: string;
}

export default function Templates() {
  const { data: currentUser, isLoading: isUserLoading } = useCurrentUser();
  const profileId = currentUser?.id;

  const {
    data: templatesRes,
    isLoading: isTemplatesLoading,
    isError,
    error,
  } = useAllTemplatesPreview(profileId);

  const isLoading = isUserLoading || isTemplatesLoading;
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [previewTemplate, setPreviewTemplate] = useState<TemplateItem | null>(null);
  const [exportTemplateSlug, setExportTemplateSlug] = useState<string | null>(null);

  // Sync API templates into local state
  useEffect(() => {
    const apiTemplatesList = Array.isArray(templatesRes)
      ? templatesRes
      : templatesRes?.data;
    if (
      apiTemplatesList &&
      Array.isArray(apiTemplatesList) &&
      apiTemplatesList.length > 0
    ) {
      const mapped = apiTemplatesList.map((t) => ({
        id: t.template_slug || (t as any).slug,
        name: t.template_name || (t as any).display_name,
        html_template: t.htm || t.html || (t as any).html_template,
      }));
      setTemplates(mapped);
    }
  }, [templatesRes]);

  return (
    <div className="w-full max-w-[1920px] mx-auto space-y-6">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <LayoutTemplate className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            Template Library
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Manage and preview enterprise resume templates.
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-800">
        {/* Loading State */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : isError ? (
          <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-600 dark:text-red-400">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span className="text-sm font-medium">
              {(error as any)?.message ||
                "Failed to load templates from server."}
            </span>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {templates.map((template) => (
              <div
                key={template.id}
                className="group border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-md transition-all bg-white dark:bg-slate-900 flex flex-col h-full"
              >
                <div className="aspect-[1/1.4] bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 p-4 relative flex items-center justify-center overflow-hidden">
                  {/* Scaled down container */}
                  <div
                    className="absolute top-0 origin-top flex justify-center w-full"
                    style={{ transform: "scale(0.3)", width: "21cm" }}
                  >
                    <div className="shadow-xl bg-white w-full h-[29.7cm] overflow-hidden p-6 text-slate-900">
                      {template.html_template ? (
                        <div
                          className="w-full h-full text-xs"
                          dangerouslySetInnerHTML={{
                            __html: template.html_template,
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-8">
                          <LayoutTemplate className="w-16 h-16 text-purple-400 mb-4" />
                          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                            {template.name}
                          </h2>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Overlay action - identical functionality, styled to match MyResumes */}
                  <div className="absolute inset-0 bg-purple-900/40 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 z-20">
                    <Button
                      size="icon"
                      onClick={() => setExportTemplateSlug(template.id)}
                      className="w-10 h-10 bg-white text-purple-700 rounded-full shadow-lg hover:bg-purple-50"
                      title="Export Template"
                    >
                      <Eye className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                <div className="p-4 border-t border-slate-200 dark:border-slate-800 mt-auto bg-white dark:bg-slate-900">
                  <h3 className="font-bold text-slate-900 dark:text-white text-sm truncate">
                    {template.name}
                  </h3>
                </div>
              </div>
            ))}

            {templates.length === 0 && (
              <div className="col-span-full py-16 text-center text-slate-500 dark:text-slate-400">
                No resume templates found.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      <Dialog
        open={!!previewTemplate}
        onOpenChange={(open) => !open && setPreviewTemplate(null)}
      >
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 p-0">
          <div className="flex justify-between items-center p-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <div>
              <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                {previewTemplate?.name}
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 dark:text-slate-400">
                Template Preview
              </DialogDescription>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-8 flex justify-center bg-slate-100 dark:bg-slate-950 relative min-h-[500px]">
            <div className="absolute top-3 right-3 bg-slate-900/80 text-white text-[10px] font-bold px-2 py-1 rounded backdrop-blur-sm z-50 uppercase tracking-wider">
              LIVE PREVIEW
            </div>
            <div
              className="w-[21cm] bg-white text-slate-900 shadow-2xl origin-top"
              style={{ transform: "scale(0.85)" }}
            >
              {previewTemplate?.html_template ? (
                <div
                  className="w-full min-h-[29.7cm] p-8"
                  dangerouslySetInnerHTML={{
                    __html: previewTemplate.html_template,
                  }}
                />
              ) : (
                <div className="w-full min-h-[29.7cm] p-8 flex flex-col justify-center items-center">
                  <LayoutTemplate className="w-20 h-20 text-purple-500 mb-4" />
                  <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                    {previewTemplate?.name}
                  </h2>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Candidates Modal with Selected Template Slug */}
      {profileId && (
        <ExportCandidatesModal
          isOpen={Boolean(exportTemplateSlug)}
          onClose={() => setExportTemplateSlug(null)}
          selectedIds={[profileId]}
          selected_slug={exportTemplateSlug || undefined}
          filename={`${currentUser?.first_name}_${currentUser?.last_name}`}
        />
      )}
    </div>
  );
}
